import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { ArrowLeft, AlertTriangle, Upload, FileText } from 'lucide-react';
import { useCreateMedRequest, useUploadMedDocument, CreateMedRequestDto, getReasonLabel } from '../../hooks/useMed';

const reasons = ['FRAUD', 'OPERATIONAL_FAILURE'] as const;

export function MedRequestCreate() {
  const navigate = useNavigate();
  const createMutation = useCreateMedRequest();
  const uploadMutation = useUploadMedDocument();
  const [error, setError] = useState<string | null>(null);
  const [files, setFiles] = useState<File[]>([]);

  const { register, handleSubmit, watch, formState: { errors, isSubmitting } } = useForm<CreateMedRequestDto>();
  const selectedReason = watch('reason');

  const onSubmit = async (data: CreateMedRequestDto) => {
    try {
      setError(null);
      const result = await createMutation.mutateAsync({
        ...data,
        requestedAmount: Math.round(Number(data.requestedAmount) * 100),
      });

      if (files.length > 0) {
        await uploadMutation.mutateAsync({ id: result.id, files });
      }

      navigate('/banking/med');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao criar solicitação');
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFiles(Array.from(e.target.files));
    }
  };

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6">
        <ArrowLeft className="w-4 h-4" /> Voltar
      </button>

      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="flex items-center gap-3 mb-6">
          <AlertTriangle className="w-8 h-8 text-orange-500" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Solicitar Devolução (MED)</h1>
            <p className="text-gray-600">Mecanismo Especial de Devolução PIX</p>
          </div>
        </div>

        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-yellow-800">
            O MED permite solicitar a devolução de valores em casos de fraude ou falha operacional.
            O prazo máximo para solicitação é de 80 dias após a transação original.
          </p>
        </div>

        {error && <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">{error}</div>}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">ID da Transação Original</label>
            <input
              type="text"
              {...register('originalTransactionId', { required: 'Informe o ID da transação' })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Ex: E12345678901234567890123456789012"
            />
            {errors.originalTransactionId && <p className="mt-1 text-sm text-red-600">{errors.originalTransactionId.message}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Valor Solicitado (R$)</label>
            <input
              type="number"
              step="0.01"
              min="0.01"
              {...register('requestedAmount', { required: 'Informe o valor', min: { value: 0.01, message: 'Valor mínimo é R$ 0,01' } })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="0,00"
            />
            {errors.requestedAmount && <p className="mt-1 text-sm text-red-600">{errors.requestedAmount.message}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Motivo da Solicitação</label>
            <select
              {...register('reason', { required: 'Selecione o motivo' })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Selecione o motivo</option>
              {reasons.map((r) => <option key={r} value={r}>{getReasonLabel(r)}</option>)}
            </select>
            {errors.reason && <p className="mt-1 text-sm text-red-600">{errors.reason.message}</p>}
            {selectedReason === 'FRAUD' && (
              <p className="mt-2 text-sm text-red-600">
                Em casos de fraude, é obrigatório anexar documentos comprobatórios (B.O., prints, etc.)
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Descrição Detalhada</label>
            <textarea
              {...register('description', { required: 'Descreva o ocorrido', minLength: { value: 50, message: 'Mínimo de 50 caracteres' } })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              rows={4}
              placeholder="Descreva detalhadamente o que aconteceu..."
            />
            {errors.description && <p className="mt-1 text-sm text-red-600">{errors.description.message}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Upload className="w-4 h-4 inline mr-1" /> Documentos Comprobatórios
            </label>
            <input
              type="file"
              multiple
              onChange={handleFileChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              accept=".pdf,.jpg,.jpeg,.png"
            />
            {files.length > 0 && (
              <div className="mt-2 space-y-1">
                {files.map((f, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-gray-600">
                    <FileText className="w-4 h-4" /> {f.name}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex gap-4 pt-4">
            <button type="button" onClick={() => navigate(-1)} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg">
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSubmitting || createMutation.isPending}
              className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50"
            >
              {isSubmitting || createMutation.isPending ? 'Enviando...' : 'Enviar Solicitação'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default MedRequestCreate;
